import { createContext, useContext, useEffect, useState, useCallback, type ReactNode } from "react";
import { api } from "./api";
import { emptyCapabilities, type Capabilities } from "@/shared/permissions";

type AuthUser = {
  userId: number;
  personId: number;
  givenName: string;
  familyName: string;
  preferredName: string | null;
  onboarded: boolean;
  roles: string[];
  // Per-resource capability map for staff (admin = full; employee = from grants; others = none).
  capabilities: Capabilities;
  // Whether this staff member may open a school's portal in read-only preview.
  canPreviewSchools: boolean;
} | null;

type AuthContextType = {
  user: AuthUser;
  loading: boolean;
  refresh: () => Promise<void>;
  logout: () => Promise<void>;
};

const AuthContext = createContext<AuthContextType | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<AuthUser>(null);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    try {
      const res = await api.api.auth.me.$get();
      if (res.ok) {
        const data = await res.json();
        if ("userId" in data) {
          setUser({
            userId: data.userId,
            personId: (data as any).personId,
            givenName: data.givenName,
            familyName: data.familyName,
            preferredName: data.preferredName,
            onboarded: data.onboarded,
            roles: data.roles,
            capabilities: (data as any).capabilities ?? emptyCapabilities(),
            canPreviewSchools: (data as any).canPreviewSchools ?? false,
          });
        } else {
          setUser(null);
        }
      } else {
        setUser(null);
      }
    } catch {
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  const logout = useCallback(async () => {
    await api.api.auth.logout.$post();
    setUser(null);
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  return (
    <AuthContext value={{ user, loading, refresh, logout }}>
      {children}
    </AuthContext>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
}
